import { AlertTriangle, ChevronRight, Download, Loader2, Paperclip, Upload } from "lucide-react";
import { memo, useEffect, useRef, useState, type ChangeEvent } from "react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn, pluralize } from "@/lib/utils";
import { formatFileSize } from "@/utils/formatFileSize";

import type { WorkspaceFile } from "../sandbox/client/api";
import { AssistantCard } from "../theme";
import { FileName } from "./FileName";
import { useNotice } from "./notice";
import { previewKind } from "./preview/kinds";
import { WorkspacePreviewDialog } from "./preview/WorkspacePreviewDialog";
import { useToggleWorkspaceFile, useWorkspaceFilesInMessage } from "./useAddFiles";
import { folderLabel, panelName, useThreadWorkspace } from "./useThreadWorkspace";
import { roomMessage, workspaceRoom } from "./workspaceRoom";

/**
 * A file: its name opens the preview when it has one (see `previewKind`); it downloads, and goes
 * into the conversation (the next message carries it, as an attached document does). Its paperclip
 * shows pressed while the file is on the message, and a second press takes it off.
 */
function FileRow({
  file,
  inMessage,
  onPreview,
  onDownload,
  onAddToContext,
}: {
  file: WorkspaceFile;
  inMessage: boolean;
  onPreview: () => void;
  onDownload: () => void;
  onAddToContext: () => void;
}) {
  const name = panelName(file.path);
  return (
    <li className="flex min-h-9 items-center gap-2 px-3.5 py-1 text-xs">
      {previewKind(file) ? (
        <button
          type="button"
          aria-label={`Preview ${name}`}
          className="min-w-0 flex-1 break-words rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:min-h-10"
          onClick={onPreview}
        >
          <FileName name={name} />
        </button>
      ) : (
        <span className="min-w-0 flex-1 break-words">
          <FileName name={name} />
        </span>
      )}
      <span className="shrink-0 text-muted-foreground">{folderLabel(file)}</span>
      <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
        {formatFileSize(file.size)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "h-7 w-7 max-sm:h-10 max-sm:w-10",
          inMessage
            ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            : "text-muted-foreground"
        )}
        aria-label={`Add ${name} to context`}
        aria-pressed={inMessage}
        title="Add to context"
        onClick={onAddToContext}
      >
        <Paperclip className="!size-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground max-sm:h-10 max-sm:w-10"
        aria-label={`Download ${name}`}
        onClick={onDownload}
      >
        <Download className="!size-3.5" />
      </Button>
    </li>
  );
}

function ThreadFilesPanel({
  threadId,
  className,
}: {
  threadId: number | null;
  className?: string;
}) {
  const { workspace, files, usage, listing, retry, upload, read, download } =
    useThreadWorkspace(threadId);
  const { setNotice } = useNotice();
  const toggleInMessage = useToggleWorkspaceFile();
  const inMessage = useWorkspaceFilesInMessage(threadId, workspace?.id ?? null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [added, setAdded] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState<WorkspaceFile | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  // Until the first listing comes, the workspace's own count stands in for it.
  const count = listing === "ready" ? files.length : (workspace?.files ?? 0);
  if (!count) return null;
  const full = workspaceRoom(usage) === "full";

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    event.target.value = "";
    if (!chosen.length) return;
    setNotice(null);
    setUploading(true);
    const names: string[] = [];
    let problem: string | null = null;
    try {
      // One at a time: each answer's usage goes into the cache, and the next file is checked
      // against it.
      for (const file of chosen) names.push(panelName((await upload(file)).path));
    } catch (error) {
      problem = (error as Error).message;
    }
    // Another thread may show by now: this panel is gone, and the composer's notice is that thread's.
    if (!live.current) return;
    if (problem) setNotice(problem);
    setUploading(false);
    setAdded(names);
    if (names.length) setOpen(true);
  };

  const onDownload = (file: WorkspaceFile) => {
    download(file).catch(() => setNotice(`Couldn't download ${panelName(file.path)}.`));
  };
  const toggleContext = async (file: WorkspaceFile) => {
    if (threadId == null || !workspace) throw new Error("No workspace yet.");
    await toggleInMessage(threadId, workspace.id, file.path, inMessage.get(file.path) ?? null);
  };
  const onAddToContext = (file: WorkspaceFile) => {
    toggleContext(file).catch(() => setNotice(`Couldn't add ${panelName(file.path)}`));
  };

  return (
    <AssistantCard data-agent-files className={cn("mb-2", className)}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center gap-1 px-2 py-1">
          <CollapsibleTrigger
            aria-label={`Files, ${pluralize(count, "file")}`}
            className="flex min-h-8 min-w-0 flex-1 items-center gap-1.5 rounded px-1.5 text-xs font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:min-h-10"
          >
            Files
            <span className="font-normal text-muted-foreground">{count}</span>
            <ChevronRight
              className={cn(
                "h-3 w-3 text-muted-foreground transition-transform duration-fade",
                open && "rotate-90"
              )}
              aria-hidden
            />
          </CollapsibleTrigger>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground max-sm:h-10 max-sm:w-10"
            aria-label="Upload files"
            title={full ? (roomMessage(usage) ?? undefined) : undefined}
            disabled={uploading || full}
            onClick={() => picker.current?.click()}
          >
            {uploading ? <Loader2 className="motion-safe:animate-spin" /> : <Upload />}
          </Button>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            data-testid="files-panel-upload"
            onChange={(event) => void onPick(event)}
          />
        </div>
        {listing === "failed" && (
          <div
            role="alert"
            className="flex items-center gap-2 px-3.5 pb-2 text-xs text-destructive"
          >
            Couldn't load the files.
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={retry}
            >
              Retry
            </Button>
          </div>
        )}
        <p
          role="status"
          className={cn("px-3.5 pb-2 text-xs text-muted-foreground", !added.length && "sr-only")}
        >
          {added.length ? `Added ${added.join(", ")}` : ""}
        </p>
        <CollapsibleContent>
          {listing === "loading" && (
            <div role="status" className="flex justify-center border-t py-2">
              <Loader2
                className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
                aria-hidden
              />
              <span className="sr-only">Loading files</span>
            </div>
          )}
          {/* Five and a half rows: the half-cut last one says there is more. */}
          <ul className="max-h-[12.625rem] overflow-y-auto border-t py-1 max-sm:max-h-[16.75rem]">
            {files.map((file) => (
              <FileRow
                key={file.path}
                file={file}
                inMessage={inMessage.has(file.path)}
                onPreview={() => setPreviewing(file)}
                onDownload={() => onDownload(file)}
                onAddToContext={() => onAddToContext(file)}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
      <WorkspacePreviewDialog
        file={previewing}
        read={read}
        onDownload={download}
        inContext={previewing ? inMessage.has(previewing.path) : false}
        onToggleContext={toggleContext}
        onClose={() => setPreviewing(null)}
      />
    </AssistantCard>
  );
}

/**
 * The workspace's files above the composer: what the agent published and what the user uploaded,
 * folded until opened. Each downloads; an image, PDF, CSV, Markdown or JSON file also previews (HTML
 * and SVG never: nothing is rendered on our origin as markup). Upload adds to uploads/. Hidden
 * while there are none. Each thread starts afresh: folded, nothing uploading, no preview open.
 */
// Memoised: it sits in the composer, which re-renders on every keystroke.
export const FilesPanel = memo(function FilesPanel(props: {
  threadId: number | null;
  className?: string;
}) {
  return <ThreadFilesPanel key={props.threadId ?? "none"} {...props} />;
});

/**
 * The composer's line about the workspace's room: a warning when it is nearly full, an alert when
 * it is full (uploads are refused, and so is anything else written to it). Nothing otherwise.
 */
export const WorkspaceRoomNotice = memo(function WorkspaceRoomNotice({
  threadId,
}: {
  threadId: number | null;
}) {
  const { usage } = useThreadWorkspace(threadId);
  const message = roomMessage(usage);
  if (!message) return null;
  const full = workspaceRoom(usage) === "full";
  return (
    <p
      role={full ? "alert" : "status"}
      className={cn(
        "flex items-start gap-1.5 px-3.5 pt-1 text-xs",
        full ? "text-destructive" : "text-warning"
      )}
    >
      <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
      {message}
    </p>
  );
});
